import React from "react";
import styled from "styled-components";

const Section = styled.section`
  padding: 2rem 2rem;
  line-height: 1.6;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: ${({ theme }) => theme.text};
  background-color: ${({ theme }) => theme.background};

  h2 {
    color: ${({ theme }) => theme.text};
    font-size: 1.8rem;
    margin-bottom: 1rem;
  }

  h1 {
    color: ${({ theme }) => theme.text};
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  /* Table of contents links */
  .toc {
    margin-bottom: 2rem;
    padding: 1rem;
    background: ${({ theme }) => theme.tocBg};
    border: 1px solid #ddd;
    border-radius: 6px;
    color: ${({ theme }) => theme.tocText};

    ul {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        margin: 0.5rem 0;

        a {
          text-decoration: none;
          color: ${({ theme }) => theme.tocLink};
          font-weight: 600;

          &:hover {
            text-decoration: underline;
            color: ${({ theme }) => theme.tocLinkHover};
          }
        }
      }
    }
  }

  h4 {
    margin-top: 2rem;
    font-weight: bold;
    color: ${({ theme }) => theme.headerText};
  }


  p {
    margin-top: 1rem;
    white-space: pre-line;
    font-size: 1.1rem;
    color: ${({ theme }) => theme.ParagraphText};
  }
     h2, h3, h4 {
    scroll-margin-top: 60px;
  }
`;

export default function DataUploadsPage() {
  return (
    <Section>
      <h2>Production Lot Data: A Summary of Steps</h2>

      {/* Table of Contents */}
      <nav className="toc" aria-label="Page navigation">
        <ul>
          <li><a href="#step-summary">The Three Steps-Summary</a></li>
          <li><a href="#step-1">Step 1 – Upload</a></li>
          <li><a href="#step-2">Step 2 – Checking / Verifying / Validating</a></li>
          <li><a href="#step-3">Step 3 – Conversion and Integration</a></li>
          <li><a href="#data-uploads-2024">Data Uploads Documentation-2024</a></li>
          <li><a href="#appendix">Appendix</a></li>
        </ul>
      </nav>

      {/* Three Steps Summary */}
      <h1 id="step-summary">Production Lot Uploads: The Three Steps – Summary</h1>
      <p>
        A user has added new survey record. What we have so far is a veterinarian, vet group, herd and year in that record.<br />
        We need a lot more information for the herd. For the current survey the way to add more information to the survey is by uploading a spreadsheet of data.<br />
        The data to be uploaded is called "Production Lot Data" and the production lot records have associated drug information.<br />
        The spreadsheet contains important information. Each spreadsheet can represent different data depending on the source veterinarian.<br />
        Here we upload the spreadsheet of data into the production database.<br />
        Once a spreadsheet is selected and open, it is not automatically loaded into the database.<br />
        Every site keeps provides there data in some form or another.<br />
        However, every site does not seem to have the same structure compared with other sites.<br />
        It is possible, a particular site's data for a specific year may be different to the next year.<br />
        Many variations of what the source data should be and what is actually in the data can occur<br />
        Because of this we need to check the data. The user can prepare a spreadsheet to make it presentable to the database.<br />
        However, I found even after preparing a spreadsheet, it is possible the data does not match what we need. Thus the second step is to check.<br />
        Once a spreadsheet is checked and declared ready to go, then the final step is to actually "integrate" the data into the production database tables.<br />
        Once the production lot screen opens, you see the screen setup as three steps as you glance from left to right.<br /><br />
        If you have a problem opening the spreadsheet, you may have to install a MS Office tools.<br />
        I have this file available if required: AccessDatabaseEngine2010.exe<br />
        You can google "download AccessDatabaseEngine2010" and you should be taken to the MS Downloads.
      </p>

      {/* Step 1 */}
      <h1 id="step-1">Production Lot Uploads: Step 1 – Upload</h1>
      <p>
        Upload, what are we doing?<br />
        Each site provides data for the survey in a spreadsheet format.<br />
        The spreadsheet format is to be an excel file.<br />
        A spreadsheet arrives in the Project Managers office.<br />
        Each spreadsheet when it arrives will have provided production lot core data along with drug data.<br />
        Step 1 upload does not assume the each spreadsheet contains core data + drug data for one and only 1 drug route.<br />
        The data in the spreadsheet will need to "fit" into the database which has pre-defined fields.<br />
        The database provides the common data structure for all site data. When all data for each year is integrated and common, lots of nice reporting can occur.<br />
        During the first step all we want to do is upload the spreadsheet into the database, whatever format it comes as!<br /><br />
        Once the data is uploaded it will be in a format the database knows. In this case it will be PostGreSQL.<br /><br />
        The Production Lot form will open. It is opened from the Survey form. You will have to be on a specific survey record before this form will open.<br />
        When the form opens you will see at the top the Group, Year, Vet Code, Herd Code and Project Code for the survey.<br />
        The uploaded spreadsheet will pertain to only this survey, please take note!<br />
        On the left hand side of the screen you will see a panel labelled "Step 1: Upload".<br />
        Below you will see 2 tabs. The first tab is labelled "Upload History". Here we keep track of every upload that has occurred.<br />
        The second tab is labelled "Upload File". To upload a new spreadsheet click on "Upload File" tab.<br />
        Go to "Upload File" tab.<br />
        Click on "Chose File" button which has a little picture of a file beside it.<br />
        Find the file you want to open. At this stage, we have opened the Workbook. It is not uploaded yet!<br />
        Once a file is chosen, you will see the workbook name or actual file name of the excel spreadsheet uploaded.<br />
        There is a line labelled "Select Worksheet". Click the drop down box and select the worksheet you want to upload.<br />
        Next go to line where it says "Assign Drug Route". Choose one of either Bolus, Feed or Injectable.<br />
        If you are uploading more than one drug at a time choose "Mix".<br />
        Once a drug route is chosen, click on button just to the right. It will open the spreadsheet and grab information about the spreadsheet.<br />
        The final thing you need to do is choose a "Structure Type".<br />
        Choose one of either "By Columns", "By Rows" or "Mix Columns & Rows".<br />
        Once you have made all the selections you will see a button to the right of "Structure Type" with a lightning bolt on it.<br />
        Click on the button. You will upload the spreadsheet data into the database into a temporary table.<br />                    
        The upload will go through the spreadsheet grabbing the data from every column of every row and inserting it into an "Upload" repository of spreadsheets.<br />
        During the upload an attempt is made to create the table structure having date columns into date fields in the database and so on.<br />
        Once the upload is completed, you will be automatically taken to the upload history tab in Step 1.<br />
        There you will see a listing of your spreadsheet data.<br />
        An upload history record will be created so you can always go back to open the data later.<br />
        If you select the history entry you will be able to see that data in Step 2 "Uploaded Table" tab.<br />
        If you do this a replica of the spreadsheet data will be available from the database upload repository for viewing.<br />
        There are reasons to do this as we will get to next.
      </p>

      {/* Step 2 */}
      <h1 id="step-2">Production Lot Uploads: Step 2 – Checking / Verifying / Validating</h1>
      <p>
        The next step is to check the data. To check we need to open a database table from a previously uploaded spreadsheet of data<br />
        Temporarily go to Step 1 and click on the tab "Upload History". Choose the table you want to open.<br />
        Now back to Step 2 and click on "Uploaded Table". Click on the button with a green top and orange bottom, like a carrot.<br />
        After clicking on it you will see all the data in the table which was uploaded.<br /><br />
        Now on Step 2 click on tab "Check Upload". You will see "Select a Template" and a drop down box just below it.<br />
        Select the Template you have prepared previously. The templates listed are specific to the Site or Vet associated with the project.<br />
        If a template is not available there is a button above to the left of label "Step 2: Checking / Verifying / Validating".<br />
        Click on that button to open the Template Management form.<br />
        To learn more about how to setup a template click on the menu item on the left called "Templates".<br /><br />
        Assume you have selected a template and have an open data table.<br />
        Just below your template selection is a label "Click to begin checking:" and a button to the right with a check mark on it.<br />
        Click on the button and the checking process will begin. You will be prompted if you want to proceed.<br />
        Click on Yes and the checking will happen. It is important to note the checking is happening on the server.<br />
        There is very little computation happening on your workstation. Just sit back and wait. Once it is completed information appears below.<br />
        Below you will see a label "Step 2: Check Diagnostics". There you will see how many rows there are, how many columns, and so on.<br />
        If the check came up with errors or omissions then you can go through the different tabs of information.<br />
        Each tab has specific information about what was checked and what happened. The final tab is "Error Messages".<br />
        The bottom of the first tab labelled "Diagnostics" has a statistic called "Total # of errors reported:". If it reports 0 we are usually good.<br />
        If it is not zero, then look at the 4 rows of diagnostic statistics above counting a number of different things.<br />
        If there are errors after a check, it is not a good idea to upload the data. Get the data as clean as you can.<br /><br />
        The final tab to take note of on Step 2 is "Check History" tab. If you go into that tab, you can open up a list of<br />
        previously checked data. There may be 1 or more check histories for each production lot table uploaded.<br />
        The reason to do this is you may have to check several times to finally clean up your mistakes or update a template.<br />
        Please note you can check one data table more than once.<br /><br />
        For the final step, to upload the data into the database, you will start here at Step 2: Check History tab.
      </p>

      {/* Step 3 */}
      <h1 id="step-3">Production Lot Uploads: Step 3 – Conversion and Integration</h1>
      <p>
        To begin integrating the data into the database, go to Step 2: Check History tab. Open the list of checks.<br />
        They are sorted in descending order. The rows are color coded. A deep green and money green row are good enough to be uploaded.<br />
        Select which data check you want to use to integrate the data.<br />
        Now go to the Step 3: Conversion and Integration section on the right.<br />
        There are 2 tabs. The first tab is "Integrate Checked Data". The second tab is "Integrate History".<br />
        To upload checked data go to Step 3: Integrate Checked Data. I will repeat make sure you have selected a checked record from Step 2 Check History.<br />
        Under Step 3 you will see a label "Click to prepare to upload and integrate data into production:" and button to the right of the label.<br />
        Click on the button. You see some diagnostics if an attempt to upload table previously and if so how many times.<br />
        If it shows it was successful it is a good idea to not upload the data again.<br />
        Below this you will see a label "Click to begin uploading and integrating data into production:" with a button to the right with a + on it.<br />
        Click on that button and you are prompted if you want to upload the data and integrate into the database.<br />
        Click on Yes. All the work will be done on the server, not on the user's workstation. Once completed you will see a row of tabs at the bottom.<br />
        Click on tab "Integrated Diagnostics". Please take note if any errors occurred.<br />
        You can upload a data table several times. The integration will not overwrite existing records. If it finds any core or drug<br />
        data it will document it found these records, skip over them and move to the next record.<br />
        You have uploaded data into the database.<br /><br />
        If you want to see the data, close the Production Lot form and return to the survey. Refresh your records and you will see your new data.
      </p>


            {/* Data Uploads Documentation – 2024 */}
      <h1 id="data-uploads-2024">Data Uploads Documentation – 2024</h1>
       {/* Overview */}
      <section>
        <h2 className="text-xl font-semibold mt-4">Overview</h2>
        <p>
          For 2024, data arrives into the project in <strong>MS Excel spreadsheets</strong>
          (workbooks with one or more worksheets).
        </p>
        <p>
          We need to ensure that the source spreadsheet data is uploaded accurately into the project database.
        </p>
        <p>
          Upload <strong>scripts differ per site</strong> because each clinic organizes data
          differently. A standard format was provided, but after 6 years of uploading, each
          veterinarian has settled on a consistent format for their site — though the “shape” of the
          source data still differs between vets.
        </p>
        <p>The following summarizes:</p>
        <ul className="list-disc pl-6">
          <li>What source data we receive.</li>
          <li>How it is organized.</li>
          <li>How scripts and templates process and upload it.</li>
        </ul>
      </section>

      {/* CVC */}
      <section>
        <h2 className="font-semibold mt-6">
          Coaldale Veterinary Clinic (CVC) – Group Id = 4
        </h2>
        <p>
          <strong>Source:</strong> 1 workbook. <br />
          <strong>Herds:</strong> 8 herds. <br />
          <strong>Worksheets:</strong> 1 worksheet per herd.
        </p>
        <h3 className="font-semibold mt-4">Data Layout:</h3>
        <ul className="list-disc pl-6">
          <li>Core (production lot) data = first columns.</li>
          <li>Drug data = last columns.</li>
          <li>Each row includes both core and drug details.</li>
          <li>A column identifies drug route.</li>
          <li>Core data repeats for each row of drug data.</li>
        </ul>
        <h3 className="font-semibold mt-4">Templates:</h3>
        <ul className="list-disc pl-6">
          <li>1 template for core data.</li>
          <li>1 template for drug data.</li>
        </ul>
        <h3 className="font-semibold mt-4">Scripts:</h3>
        <p>
          Take templates, map fields, upload and integrate into database. Once uploaded, reports are
          prepared for analysis.
        </p>
        <h3 className="font-semibold mt-4">Special Case:</h3>
        <ul className="list-disc pl-6">
          <li>
            Each worksheet requires inserting a separator column between the end of the core data
            and the start of the drug data.
          </li>
          <li>Column name = <code>change_to_rows</code>.</li>
          <li>Value for each row = <code>&lt;&lt;row&gt;&gt;</code>.</li>
          <li>CVC is the only source requiring this step.</li>
        </ul>
      </section>

      {/* FHMS */}
      <section>
        <h2 className="font-semibold mt-6">
          Feedlot Health Management Services (FHMS) – Group Id = 1
        </h2>
        <p>
          <strong>Source:</strong> 1 workbook. <br />
          <strong>Herds:</strong> 10 herds.
        </p>
        <h3 className="font-semibold mt-4">Worksheets:</h3>
        <ul className="list-disc pl-6">
          <li>Core data (all herds).</li>
          <li>Drug data – Feed (all herds).</li>
          <li>Drug data – Injectable (all herds).</li>
        </ul>
        <h3 className="font-semibold mt-4">Data Layout:</h3>
        <ul className="list-disc pl-6">
          <li>Each drug record (feed or injectable) must match with a production lot code in core data.</li>
          <li>This ensures related records stay properly linked.</li>
        </ul>
        <h3 className="font-semibold mt-4">Templates:</h3>
        <ul className="list-disc pl-6">
          <li>1 template for core data.</li>
          <li>1 template for feed drug data.</li>
          <li>1 template for injectable drug data.</li>
          <li>No template for bolus drug data in 2024.</li>
        </ul>
        <h3 className="font-semibold mt-4">Scripts:</h3>
        <p>
          Require specifying the correct template and associated worksheet for each type (core,
          feed, injectable). Upload, integrate, then prepare reports.
        </p>
      </section>

      {/* VAHS */}
      <section>
        <h2 className="text-xl font-semibold mt-6">
          Veterinary Agri-Health Services (VAHS) – Group Id = 2
        </h2>
        <p>
          <strong>Source:</strong> 3 workbooks (1 per herd). <br />
          <strong>Herds:</strong> 3 herds.
        </p>
        <h3 className="font-semibold mt-4">Worksheets per herd:</h3>
        <ul className="list-disc pl-6">
          <li>Core data.</li>
          <li>Drug data – Feed.</li>
          <li>Drug data – Injectable.</li>
        </ul>
        <h3 className="font-semibold mt-4">Data Layout:</h3>
        <ul className="list-disc pl-6">
          <li>Same logic as FHMS.</li>
          <li>Each drug record must match to a production lot code in the core data.</li>
        </ul>
        <h3 className="font-semibold mt-4">Templates:</h3>
        <ul className="list-disc pl-6">
          <li>1 template for core data.</li>
          <li>1 template for feed drug data.</li>
          <li>1 template for injectable drug data.</li>
        </ul>
        <h3 className="font-semibold mt-4">Scripts:</h3>
        <p>
          Similar to FHMS, but slightly adjusted for per-herd workbooks. Must specify the correct
          template and worksheet per herd. Upload, integrate, then prepare reports.
        </p>
      </section>

      {/* Key Takeaways */}
      <section>
        <h2 className="text-xl font-semibold mt-6">Key Takeaways</h2>
        <ul className="list-disc pl-6">
          <li>
            <strong>Differences matter</strong> – each site organizes data differently, so
            processing must adapt.
          </li>
          <li>
            <strong>Templates vary</strong> – source-to-database mapping must be site-specific.
          </li>
          <li>
            <strong>Scripts handle complexity</strong> – templates provide field mapping, while
            scripts adapt to each site’s structure.
          </li>
          <li>
            <strong>Effort invested</strong> – years of refinement ensure diverse data formats are
            transformed into a consistent, fixed database structure.
          </li>
        </ul>
      </section>

      <h1 id="appendix">Appendix</h1>
      <p>
        Appendix: Spreadsheet Column Names and Database Field Names
        <br />
        During the process of uploading spreadsheet data into the database, the application reads the first row of spreadsheet columns
        and uses those column names as field names in the database. Sometimes, a spreadsheet column name is too long and has to be truncated.
        The recommendation made is to limit the length of spreadsheet columns to 55 characters or less if possible.
        There are other column names which match the database "reserved words" and as such can be problematic using that name in the database.
        There are ways around this, however we have chosen to alter some names to get around the reserved word problem. The final problem encountered
        is there are characters in the column name which are not allowed in a database field name. I will list these exceptions here for reference.
      </p>

      <h5>Reserved Words:</h5>
      <ul>
        <li>Column Name "in" set to Field Name "n_in"</li>
        <li>Column Name "out" set to Field Name "n_out"</li>
        <li>Column Name "type" set to Field Name "type_uk"</li>
      </ul>

      <h5>Invalid Character Transformations:</h5>
      <ul>
        <li>Comma "," set to null ""</li>
        <li>Period "." set to null ""</li>
        <li>Tilde "~" set to null ""</li>
        <li>Exclamation "!" set to null ""</li>
        <li>At sign "@" set to null ""</li>
        <li>Number "#" set to null ""</li>
        <li>Question mark "?" set to null ""</li>
        <li>Caret "^" set to null ""</li>
        <li>Ampersand "&" set to null ""</li>
        <li>Open bracket "(" set to null ""</li>
        <li>Closed bracket ")" set to null ""</li>
        <li>Minus sign "-" set to null ""</li>
        <li>Plus sign "+" set to null ""</li>
        <li>Asterisk "*" set to null ""</li>
        <li>Equals "=" set to null ""</li>
        <li>Apostrophe "`" set to null ""</li>
        <li>Double quote """ set to null ""</li>
        <li>Less than &quot;&lt;&quot; set to null &quot;&quot;</li>
        <li>Greater than ">" set to null ""</li>
        <li>Colon ":" set to null ""</li>
        <li>Semi-colon ";" set to null ""</li>
        <li>Backslash "/" set to null ""</li>
        <li>Foreslash "\" set to null ""</li>
        <li>Space " " set to underscore "_"</li>
        <li>Percent "%" set to abbreviation "_pct_"</li>
      </ul>

      <p>
        Note: I continue to expand these lists as upload problems arise. I will document those changes.
      </p>
    </Section>
  );
}